import React from 'react'
import { Helmet } from 'react-helmet-async'

const SITE_URL = 'https://badanibex.com'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'product'
  locale?: 'ar_SA' | 'en_US'
  canonical?: string
  noindex?: boolean
  nofollow?: boolean
  structuredData?: object | null
}

const SEO: React.FC<SEOProps> = ({
  // البيانات الافتراضية المستخرجة مباشرة من ملف الـ HTML
  title = 'BadanIbex® قوالب ويب ولوحات تحكم وموارد إبداعية للاستخدام المباشر',
  description = 'ارتقِ بقدراتك الإبداعية وأطلق منصتك الرقمية بسرعة أكبر مع قوالب ويب ولوحات تحكم وأنظمة SaaS وتطبيقات وموارد إبداعية جاهزة للاستخدام. اختصر وقت التطوير وابنِ مشاريعك الرقمية باحترافية مع BadanIbex®.',
  keywords = 'BadanIbex®, قوالب ويب, قوالب مواقع, لوحات تحكم, أنظمة SaaS, تطبيقات ويب, أنظمة ويب, منتجات رقمية, موارد إبداعية, قوالب برمجية, واجهات ويب, مشاريع جاهزة, Laravel, React, Dashboard, SaaS Templates, Web Templates',
  image = 'https://badanibex.com/og-image.jpg',
  url = 'https://badanibex.com/',
  type = 'website',
  locale = 'ar_SA',
  canonical,
  noindex = false,
  nofollow = false,
  structuredData
}) => {
  const fullUrl = url.startsWith('http') ? url : `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`
  const canonicalUrl = canonical ?? fullUrl
  const fullImageUrl = image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`

  return (
    <Helmet>
      {/* Core Meta & Author */}
      <meta name="author" content="Fahd Ali - BadanIbex®" />
      <meta name="language" content="Arabic, English" />
      <meta name="geo.region" content="SA" />
      <meta name="geo.placename" content="Saudi Arabia" />

      {/* Google Verification */}
      <meta
        name="google-site-verification"
        content="xzjrvMTL3TYYqXUcsKoN6eUzpwiUhrCQxJZntgGw3ZQ"
      />

      {/* Primary SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical & Hreflang */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      <link rel="alternate" hrefLang="ar-SA" href={canonicalUrl} />

      {/* Robots Directives */}
      {noindex || nofollow ? (
        <meta
          name="robots"
          content={`${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`}
        />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Open Graph (Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta
        property="og:image:alt"
        content="BadanIbex® — قوالب ويب ولوحات تحكم وموارد إبداعية"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content="BadanIbex®" />

      {/* Twitter Cards / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@badanibex" />
      <meta name="twitter:creator" content="@badanibex" />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  )
}

export default SEO